import React from 'react';
import Switch from 'react-switch';
import styled from 'styled-components';
import type { Locale } from '@peloton/internationalize';
import { white, gray3, superRedA11y, gray1 } from '@engage/colors';
import type { ClassLanguage } from '@engage/settings';
import { spaces } from '@engage/styles';
import { header5Small } from '@engage/typography/headers';
import { Collapsible } from '@members/accordions';
import { isLanguageEnabled } from './localReducers';
import { SubtitleOptions } from './SubtitleOptions';

export const LanguageOption: React.FC<React.PropsWithChildren<Props>> = ({
  language,
  displayAllClasses,
  displayOnlySubtitles,
  toggleSubtitle,
  enableLanguage,
  disableLanguage,
}) => {
  const languageEnabled = isLanguageEnabled(language);

  return (
    <LanguageCard>
      <LanguageToggle>
        <LanguageText htmlFor={language.locale}>{language.localeName}</LanguageText>
        <Switch
          id={language.locale}
          checked={languageEnabled}
          aria-checked={languageEnabled}
          role="switch"
          onChange={isChecked => (isChecked ? enableLanguage() : disableLanguage())}
          offColor={gray3}
          onColor={superRedA11y}
          uncheckedIcon={false}
          checkedIcon={false}
          height={25}
          width={40}
          boxShadow={boxShadowStyle}
        />
      </LanguageToggle>
      <Collapsible
        isOpen={languageEnabled}
        recalculateHeightOn={language.displayAllClasses}
      >
        {languageEnabled ? (
          <>
            <Divider />
            <SubtitleOptions
              language={language}
              displayAllClasses={displayAllClasses}
              displayOnlySubtitles={displayOnlySubtitles}
              toggleSubtitle={toggleSubtitle}
            />
          </>
        ) : null}
      </Collapsible>
    </LanguageCard>
  );
};

const boxShadowStyle =
  '0 4px 9px 2px rgba(0, 0, 0, 0.08), 0 3.5px 1.5px 0 rgba(0, 0, 0, 0.1), 0 0 1px 0.5px rgba(0, 0, 0, 0.11), 0 0.5px 1px 0 rgba(0, 0, 0, 0.07)';

const LanguageCard = styled.div`
  width: 100%;
  background-color: ${white};
  border-radius: ${spaces.xxSmall}px;

  &:not(:first-child) {
    margin-top: ${spaces.small}px;
  }
`;

const LanguageToggle = styled.div`
  width: 100%;
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${spaces.small}px;
`;

const LanguageText = styled.label`
  ${header5Small}
  pointer-events: none;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${gray1};
`;

type Props = {
  language: ClassLanguage;
  displayAllClasses: () => void;
  displayOnlySubtitles: () => void;
  toggleSubtitle: (subtitleLocale: Locale) => void;
  enableLanguage: () => void;
  disableLanguage: () => void;
};
